import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Container,
    Paper,
    Stack,
    Text,
    Title,
    Loader,
    Center,
    Badge
} from "@mantine/core";
import QRGenerator from "../../../components/QRGenerator";

type ParkingQrInfoDto = {
    isValid: boolean;
    message?: string;
    eventName?: string;
    validDate?: string;
    isUsed?: boolean;
    usedAt?: string;
    note?: string;
};

export default function ParkingQrPassPage() {
    const apiUrl = process.env.REACT_APP_API_URL;
    const webUrl = process.env.REACT_APP_WEB_URL;
    const { token } = useParams();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<ParkingQrInfoDto | null>(null);

    useEffect(() => {
        loadInfo();
    }, [token]);

    async function loadInfo() {
        if (!apiUrl || !token) {
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const res = await fetch(`${apiUrl}/api/parking/qr/info/${token}`);
            const json = await res.json();
            setData(json);
        } catch {
            setData({
                isValid: false,
                message: "Could not load QR code."
            });
        } finally {
            setLoading(false);
        }
    }

    const scanUrl = token ? `${webUrl}/parking-scan/${token}` : "";

    return (
        <Container size="sm" py="xl">
            <Paper withBorder p="xl" radius="lg">
                {loading ? (
                    <Center>
                        <Stack align="center">
                            <Loader color="red" />
                            <Text>Loading parking pass...</Text>
                        </Stack>
                    </Center>
                ) : !data?.isValid ? (
                    <Stack align="center" gap="md">
                        <Badge color="red" size="xl" variant="filled">
                            INVALID
                        </Badge>

                        <Title order={2} ta="center">
                            QR NOT AVAILABLE
                        </Title>

                        <Text ta="center">
                            {data?.message || "This parking QR code could not be loaded."}
                        </Text>
                    </Stack>
                ) : (
                    <Stack align="center" gap="md">
                        <Title order={2} ta="center">
                            PARKING PASS
                        </Title>

                        {data.isUsed ? (
                            <Badge color="red" size="lg" variant="filled">
                                ALREADY USED
                            </Badge>
                        ) : (
                            <Badge color="green" size="lg" variant="filled">
                                READY TO SCAN
                            </Badge>
                        )}

                        <Text fw={600}>{data.eventName}</Text>

                        {data.validDate && (
                            <Text c="dimmed">
                                Valid date: {new Date(data.validDate).toLocaleDateString("en-GB")}
                            </Text>
                        )}

                        {!data.isUsed && scanUrl && (
                            <QRGenerator value={scanUrl} />
                        )}

                        {data.isUsed && data.usedAt && (
                            <Text size="sm" c="dimmed" ta="center">
                                Used at: {new Date(data.usedAt).toLocaleString("en-GB")}
                            </Text>
                        )}

                        {data.note && (
                            <Text ta="center">{data.note}</Text>
                        )}

                        <Text size="sm" c="dimmed" ta="center">
                            Please show this QR code on arrival.
                        </Text>
                    </Stack>
                )}
            </Paper>
        </Container>
    );
}